import { Fingerprint, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { authClient } from "@/lib/auth-client";

interface PasskeyItem {
  id: string;
  name: string | null;
  deviceType: string;
  createdAt: Date | null;
  aaguid: string | null;
}

export function PasskeySection() {
  const [passkeys, setPasskeys] = useState<PasskeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const loadPasskeys = useCallback(async () => {
    const result = await authClient.passkey.listUserPasskeys();
    if (result.data) {
      setPasskeys(result.data as unknown as PasskeyItem[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadPasskeys();
  }, [loadPasskeys]);

  async function handleAddPasskey() {
    setAdding(true);
    setError("");

    const result = await authClient.passkey.addPasskey({
      name: `Passkey ${passkeys.length + 1}`,
    });

    setAdding(false);

    if (result.error) {
      setError(result.error.message ?? "Failed to add passkey");
      return;
    }

    await loadPasskeys();
  }

  async function handleDeletePasskey(id: string) {
    setError("");

    const result = await authClient.passkey.deletePasskey({ id });

    if (result.error) {
      setError(result.error.message ?? "Failed to delete passkey");
      return;
    }

    setPasskeys((prev) => prev.filter((p) => p.id !== id));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Fingerprint className="size-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Passkeys</h2>
        </div>
        <button
          type="button"
          onClick={handleAddPasskey}
          disabled={adding}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-muted transition-colors disabled:opacity-50"
        >
          <Plus className="size-3.5" />
          {adding ? "Adding..." : "Add passkey"}
        </button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <div className="rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">Loading passkeys...</p>
        </div>
      ) : passkeys.length === 0 ? (
        <div className="rounded-lg border p-4">
          <p className="text-sm text-muted-foreground">
            No passkeys yet. Add one to sign in with biometrics or a hardware key.
          </p>
        </div>
      ) : (
        <ul className="rounded-lg border divide-y">
          {passkeys.map((passkey) => (
            <li key={passkey.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <Fingerprint className="size-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {passkey.name ?? `${passkey.deviceType} passkey`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {passkey.deviceType}
                    {passkey.createdAt ? ` · ${passkey.createdAt.toLocaleDateString()}` : ""}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDeletePasskey(passkey.id)}
                className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-destructive transition-colors"
                title="Delete passkey"
              >
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
